/* eslint-disable no-console */
import {
  TrackerFactory,
  TrackerType,
  useCookie
} from '@lucid/gatsby-theme-lucid';
import throttle from 'lodash.throttle';
import React, { ReactNode, useContext, useEffect, useState } from 'react';

import AbTestContext from '../../contexts/abTestContext';
import LayoutContext from '../../contexts/layoutContext';
import { useFeatureFlags } from '../../hooks/use-feature-flags';
import './ab-test.scss';
import { getVariationAssignment } from './helpers';

export enum PossibleTrigger {
  Load = 'load',
  Click = 'click',
  Delay = 'delay',
  Redirect = 'redirect',
  Submit = 'submit',
  Scroll = 'scroll',
  Visible = 'visible'
}

export enum Variation {
  A = 'T_A',
  B = 'T_B',
  C = 'T_C'
}

const MARKETING_TEAM = 'Growth Front-end';

interface Props {
  aboveTheFold?: boolean;
  children?: ReactNode;
  defaultBranch?: Variation;
  delay?: number;
  distance?: number;
  conditionalTrigger?: boolean;
  selector?: string;
  ssrBranch?: Variation;
  test: string;
  trigger?: PossibleTrigger;
  testMode?: boolean;
  pollingFunc?: () => Promise<any>;
}

const AbTest = (props: Props) => {
  const marketingTestConfig = useFeatureFlags();

  const {
    aboveTheFold = false,
    children = null,
    defaultBranch = Variation.A,
    delay,
    distance = 0,
    conditionalTrigger = true,
    selector = '',
    ssrBranch = Variation.A,
    test,
    trigger = PossibleTrigger.Load,
    testMode = false,
    pollingFunc
  } = props;

  const featureFlag = marketingTestConfig.get(test);
  const testBranches = featureFlag ? featureFlag.variations : null;
  const initialIsActive = featureFlag
    ? Boolean(featureFlag.isActive && conditionalTrigger)
    : true;

  const [isAssigned, setIsAssigned] = useState(false);
  const [branch, setBranch] = useState<Variation>(ssrBranch);
  const [intervals, setIntervals] = useState<NodeJS.Timer[]>([]);
  const [testIsActive, setTestIsActive] = useState(initialIsActive);
  const [triggerFired, setTriggerFired] = useState(false);
  const { abTesting } = useContext(LayoutContext);
  const tracker = TrackerFactory.create(TrackerType.Beacon);

  const storageKey = `abTest_${test}`;
  let delayTimeout: NodeJS.Timeout | undefined;

  const [userId] = useCookie('userId');

  const triggerCurrentTest = () => {
    if (triggerFired || !branch) {
      return;
    }
    if (testMode) {
      return setTriggerFired(true);
    }
    const normalizedBranch = branch.replace(/_/g, '-');
    const property = {
      [test]: normalizedBranch
    };

    const event = {
      name: 'assignedToTest',
      userId: Number(userId) || -1,
      testName: test,
      testArm: normalizedBranch,
      teamName: MARKETING_TEAM
    };

    const hasUiActive = window.localStorage.getItem('showAbTestingUi') === '1';

    if (!testIsActive) {
      if (hasUiActive) {
        console.log(
          'Test is not active, but if it was it would have fired the following property for KM:',
          property
        );
        console.log(
          `%c Test is not active, but if it was it would have fired the following property for Beacon:`,
          'color: #7dc3e3; font-weight: bold;',
          event
        );
      }
      return setTriggerFired(true);
    }

    if (hasUiActive) {
      console.log('Would have fired the following property for KM:', property);
      console.log(
        `%c Would have fired the following event for Beacon:`,
        'color: #06A9F4; font-weight: bold',
        event
      );

      return setTriggerFired(true);
    }

    if (branch) {
      window.localStorage.setItem(storageKey, branch);
    }

    if (window.lucid?.kissMetrics) {
      window.lucid.kissMetrics.set(property);
    } else {
      const checkInterval = setInterval(() => {
        if (window.lucid && window.lucid.kissMetrics) {
          window.lucid.kissMetrics.set(property);
          clearInterval(checkInterval);
        }
      }, 200);
      setIntervals([...intervals, checkInterval]);
    }

    tracker
      .report(event)
      .then(response => {
        console.log('Beacon reported:', response);
        return setTriggerFired(true);
      })
      .finally(() => {
        console.log('Beacon attempted');
        return setTriggerFired(true);
      });
  };

  const updateBranch = (updatedBranch: Variation) => {
    setBranch(updatedBranch);
    setTriggerFired(false);
  };

  const updateTestInterface = () => {
    const hasUiActive = window.localStorage.getItem('showAbTestingUi') === '1';
    if (hasUiActive) {
      const currentTest = marketingTestConfig.get(test);
      const branches = currentTest ? currentTest.variations : null;
      abTesting.updateTest({
        ...props,
        branch,
        trigger,
        triggerFired,
        testIsActive,
        aboveTheFold,
        branches,
        updateBranch
      });
    }
  };

  const handleScrollTrigger = () => {
    const handleScroll = throttle(() => {
      const currentY = window.pageYOffset;
      if (currentY >= distance) {
        triggerCurrentTest();
        window.removeEventListener(trigger, handleScroll);
      }
    }, 500);
    window.addEventListener('scroll', handleScroll);
  };

  const handleVisibleTrigger = () => {
    // placeholder
  };

  const handleTrigger = async () => {
    if (pollingFunc) {
      await pollingFunc();
    }
    if (selector) {
      const elements = Array.from(document.querySelectorAll(selector));
      elements.forEach(element => {
        const triggerHandler = () => {
          triggerCurrentTest();
          element.removeEventListener(trigger, triggerHandler);
        };
        element.addEventListener(trigger, triggerHandler);
      });
    }
  };

  const handleRedirectTrigger = () => {
    triggerCurrentTest();
  };

  const handleDelayTrigger = () => {
    if (!delay) {
      return;
    }
    const delayMilliseconds = delay * 1000;
    delayTimeout = setTimeout(() => {
      triggerCurrentTest();
    }, delayMilliseconds);
  };

  const refreshTriggers = () => {
    switch (trigger) {
      case PossibleTrigger.Load:
        triggerCurrentTest();
        break;
      case PossibleTrigger.Delay:
        handleDelayTrigger();
        break;
      case PossibleTrigger.Redirect:
        handleRedirectTrigger();
        break;
      case PossibleTrigger.Scroll:
        handleScrollTrigger();
        break;
      case PossibleTrigger.Visible:
        handleVisibleTrigger();
        break;
      default:
        handleTrigger();
        break;
    }
  };

  const setup = () => {
    try {
      if (!marketingTestConfig.has(test)) {
        throw Error(`AB Test not present in feature flag config: ${test}.`);
      }

      const hasUiActive =
        window.localStorage.getItem('showAbTestingUi') === '1';

      if (conditionalTrigger) {
        const {
          branch: assignedBranch,
          testIsActive: isActive
        } = getVariationAssignment({
          test,
          testBranches,
          storageKey,
          devMode: hasUiActive
        });
        setBranch(assignedBranch);
        setTestIsActive(isActive);
        setIsAssigned(true);
      } else {
        setTriggerFired(true);
        setBranch(defaultBranch);
        setTestIsActive(false);
      }
    } catch (error) {
      setTestIsActive(false);
      console.error(error);
    }
  };

  useEffect(() => {
    setup();
    return () => {
      intervals.map(clearInterval);
      if (delayTimeout) {
        clearTimeout(delayTimeout);
      }
    };
  }, []);

  useEffect(() => {
    if (branch && isAssigned) {
      updateTestInterface();
      refreshTriggers();
    }
  }, [branch, triggerFired, isAssigned]);

  const showShield = aboveTheFold && trigger === 'load' && !triggerFired;

  const abTestContext = {
    test,
    branch,
    trigger,
    selector,
    triggerFired,
    triggerCurrentTest,
    showShield
  };

  let shouldRender =
    trigger === PossibleTrigger.Redirect && testIsActive ? triggerFired : true;
  if (typeof window === 'undefined') {
    shouldRender = true;
  }

  return (
    <AbTestContext.Provider value={abTestContext}>
      <div id={`ab-test-${test}`} className="ab-test">
        {showShield && (
          <div
            className="ab-test-shield"
            style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#FFF',
              zIndex: 10000000
            }}
          />
        )}
        {shouldRender && children}
      </div>
    </AbTestContext.Provider>
  );
};

export default AbTest;
